import { useQuery } from '@tanstack/react-query';
import { getGame, listGames } from '#shared/api/games';
import type { GameDetail, GameSummary } from '#shared/schemas/game';
import { api, isApiError } from '@/lib/api';

/**
 * Query keys and hooks for the games module. Every key starts with 'games', so one
 * `invalidateQueries({ queryKey: GAMES_KEY })` refreshes the lot.
 */

export const GAMES_KEY = ['games'] as const;

export const gameKeys = {
  all: GAMES_KEY,
  detail: (id: string) => ['games', 'detail', id] as const,
  list: (o: ListOpts = {}) => ['games', 'list', o] as const,
  live: ['games', 'live'] as const,
};

export interface ListOpts {
  status?: 'live' | 'final' | 'abandoned';
  playerId?: string;
  mode?: GameSummary['mode'];
  sessionId?: string;
  before?: number;
  limit?: number;
}

export const isNotFound = (e: unknown): boolean => isApiError(e) && e.status === 404;

export function fetchGame(id: string): Promise<GameDetail> {
  return api.call(getGame, { params: { id } });
}

export function useGameDetail(id: string, enabled = true) {
  return useQuery({
    queryKey: gameKeys.detail(id),
    queryFn: () => fetchGame(id),
    enabled,
    staleTime: 5_000,
    retry: (count, e) => !isNotFound(e) && count < 2,
  });
}

export function fetchGames(o: ListOpts = {}) {
  return api.call(listGames, { query: o });
}

/** A page of games, newest first. */
export function useGames(o: ListOpts = {}, enabled = true) {
  return useQuery({
    queryKey: gameKeys.list(o),
    queryFn: () => fetchGames(o),
    enabled,
    staleTime: 15_000,
    meta: { offline: false },
  });
}

/** Games being played right now, for the Home strip and the live hub. */
export function useLiveGames() {
  return useQuery({
    queryKey: gameKeys.live,
    queryFn: async () => (await fetchGames({ status: 'live', limit: 10 })).items,
    staleTime: 8_000,
    refetchInterval: 20_000,
    meta: { persist: false },
  });
}
